import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, CardBody, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Badge, HStack, VStack, Box, Text, Button, IconButton, Input, Select,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, FormControl, FormLabel, FormHelperText,
  Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody,
  DrawerCloseButton, DrawerFooter, Tabs, TabList, TabPanels, Tab, TabPanel,
  useDisclosure, useToast, Divider, Alert, AlertIcon, Progress, SimpleGrid,
  Stat, StatLabel, StatNumber, Wrap, WrapItem, Tag, Tooltip,
} from '@chakra-ui/react';
import {
  AddIcon, DeleteIcon, EditIcon, RepeatIcon, ViewIcon, EmailIcon,
  ChevronLeftIcon, ChevronRightIcon,
} from '@chakra-ui/icons';
import { useTranslation, Trans } from 'react-i18next';
import PageShell from '../components/PageShell';
import ConfirmModal from '../components/ConfirmModal';
import AudienceFilterBuilder from '../components/AudienceFilterBuilder';
import EmailBlockEditor, { DEFAULT_THEME, MODE_BLOCKS } from '../components/EmailBlockEditor';
import newsletterService from '../services/newsletterService';

const STATUS_COLORS = {
  draft: 'gray',
  scheduled: 'purple',
  sending: 'blue',
  sent: 'green',
  failed: 'red',
  cancelled: 'orange',
};

const RECIPIENT_STATUS_COLORS = {
  pending: 'gray', sent: 'green', failed: 'red', skipped: 'orange',
};

const PAGE_SIZE = 20;

const emptyForm = {
  name: '', subject: '', preheader: '',
  bodyMode: MODE_BLOCKS, bodyDesign: { theme: DEFAULT_THEME, blocks: [] }, bodyHtml: '',
  fromName: '', fromEmail: '', replyTo: '', audienceFilters: {},
};

// Starter body for a new campaign. Built when the editor opens (not at module
// load) so it follows the current language. The {{guest_first_name}} /
// {{hotel_name}} merge tags are passed in as values so i18next leaves them intact.
const buildStarterDesign = (t) => ({
  theme: DEFAULT_THEME,
  blocks: [
    { id: 's1', type: 'text', align: 'left', size: 15,
      text: t('newsletters.starter.greeting', { firstNameTag: '{{guest_first_name}}' }) },
    { id: 's2', type: 'text', align: 'left', size: 15,
      text: t('newsletters.starter.signOff', { hotelNameTag: '{{hotel_name}}' }) },
  ],
});

const Newsletters = () => {
  const { t } = useTranslation();
  const [campaigns, setCampaigns] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [recipientBreakdown, setRecipientBreakdown] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [placeholders, setPlaceholders] = useState([]);
  const [testEmail, setTestEmail] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);

  const editorDisclosure = useDisclosure();
  const detailDisclosure = useDisclosure();
  const deleteDisclosure = useDisclosure();
  const sendDisclosure = useDisclosure();
  const optOutDisclosure = useDisclosure();
  const toast = useToast();

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, pageSize: PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const data = await newsletterService.list(params);
      setCampaigns(data.results || []);
      setTotal(data.total || 0);
      setError('');
    } catch {
      setError(t('newsletters.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    newsletterService.placeholders()
      .then((d) => setPlaceholders(d.placeholders || []))
      .catch(() => setPlaceholders([]));
  }, []);

  const setF = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const openCreate = () => {
    setForm({ ...emptyForm, bodyDesign: buildStarterDesign(t) });
    setEditingId(null);
    setPreview(null);
    editorDisclosure.onOpen();
  };

  const openEdit = async (campaign) => {
    try {
      const full = await newsletterService.get(campaign.id);
      setForm({
        name: full.name || '',
        subject: full.subject || '',
        preheader: full.preheader || '',
        bodyMode: full.bodyMode || MODE_BLOCKS,
        bodyDesign: full.bodyDesign && full.bodyDesign.blocks
          ? full.bodyDesign
          : { theme: DEFAULT_THEME, blocks: [] },
        bodyHtml: full.bodyHtml || '',
        fromName: full.fromName || '',
        fromEmail: full.fromEmail || '',
        replyTo: full.replyTo || '',
        audienceFilters: full.audienceFilters || {},
      });
      setEditingId(full.id);
      setPreview(null);
      editorDisclosure.onOpen();
    } catch {
      toast({ title: t('newsletters.toasts.openFailed'), status: 'error', duration: 3000 });
    }
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.subject.trim()) {
      toast({ title: t('newsletters.toasts.nameSubjectRequired'), status: 'warning', duration: 3000 });
      return;
    }
    setSaving(true);
    try {
      const saved = editingId
        ? await newsletterService.update(editingId, form)
        : await newsletterService.create(form);
      setEditingId(saved.id);
      toast({ title: editingId ? t('newsletters.toasts.saved') : t('newsletters.toasts.created'),
              status: 'success', duration: 2000 });
      load();
      return saved;
    } catch (err) {
      toast({
        title: t('newsletters.toasts.saveFailed'),
        description: err.response?.data?.detail || t('newsletters.toasts.saveFailedHint'),
        status: 'error', duration: 5000,
      });
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndClose = async () => {
    const saved = await handleSave();
    if (saved) editorDisclosure.onClose();
  };

  const refreshPreview = async () => {
    setPreviewing(true);
    // Body overrides so the preview shows the editor's current state, not the
    // last save. Blocks win when we're in visual mode; the server compiles them
    // through the same path a real send uses.
    const body = form.bodyMode === MODE_BLOCKS
      ? { bodyDesign: form.bodyDesign }
      : { bodyHtml: form.bodyHtml };
    try {
      const res = editingId
        ? await newsletterService.preview(editingId, {
            subject: form.subject, preheader: form.preheader, ...body,
          })
        // Unsaved draft — render it without creating a campaign row first.
        : await newsletterService.draftPreview({
            subject: form.subject, preheader: form.preheader,
            audienceFilters: form.audienceFilters, ...body,
          });
      setPreview(res);
    } catch (err) {
      toast({ title: t('newsletters.toasts.previewFailed'),
              description: err.response?.data?.detail, status: 'error', duration: 5000 });
    } finally {
      setPreviewing(false);
    }
  };

  const handleTestSend = async () => {
    if (!testEmail.trim()) {
      toast({ title: t('newsletters.toasts.testAddressRequired'), status: 'warning', duration: 3000 });
      return;
    }
    let id = editingId;
    if (!id) {
      const saved = await handleSave();
      if (!saved) return;
      id = saved.id;
    }
    setSaving(true);
    try {
      await newsletterService.testSend(id, { email: testEmail.trim() });
      toast({ title: t('newsletters.toasts.testSent', { email: testEmail }), status: 'success', duration: 3000 });
    } catch (err) {
      const data = err.response?.data;
      toast({
        title: data?.detail || t('newsletters.toasts.testFailed'),
        description: data?.errors?.smtp || t('newsletters.toasts.testFailedHint'),
        status: 'error', duration: 8000, isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    setSaving(true);
    try {
      const res = await newsletterService.send(sendTarget.id);
      toast({
        title: res.dispatch === 'queued' ? t('newsletters.toasts.queued') : t('newsletters.toasts.sent'),
        description: t('newsletters.toasts.recipientCount', { count: res.totalRecipients }),
        status: 'success', duration: 4000,
      });
      sendDisclosure.onClose();
      editorDisclosure.onClose();
      load();
    } catch (err) {
      toast({
        title: t('newsletters.toasts.sendFailed'),
        description: err.response?.data?.detail || t('newsletters.toasts.unknownError'),
        status: 'error', duration: 8000, isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const openSendConfirm = async (campaign) => {
    setSendTarget(campaign);
    sendDisclosure.onOpen();
  };

  const handleDelete = async () => {
    try {
      await newsletterService.remove(deleteTarget.id);
      toast({ title: t('newsletters.toasts.deleted'), status: 'success', duration: 2000 });
      deleteDisclosure.onClose();
      load();
    } catch (err) {
      toast({ title: err.response?.data?.detail || t('newsletters.toasts.deleteFailed'),
              status: 'error', duration: 4000 });
    }
  };

  const handleDuplicate = async (campaign) => {
    try {
      await newsletterService.duplicate(campaign.id);
      toast({ title: t('newsletters.toasts.duplicated'), status: 'success', duration: 2000 });
      load();
    } catch {
      toast({ title: t('newsletters.toasts.duplicateFailed'), status: 'error', duration: 3000 });
    }
  };

  const handleCancel = async (campaign) => {
    try {
      await newsletterService.cancel(campaign.id);
      toast({ title: t('newsletters.toasts.cancelled'), status: 'info', duration: 2000 });
      load();
      if (detail?.id === campaign.id) openDetail(campaign);
    } catch (err) {
      toast({ title: err.response?.data?.detail || t('newsletters.toasts.cancelFailed'),
              status: 'error', duration: 4000 });
    }
  };

  const openDetail = async (campaign) => {
    try {
      const [full, recips] = await Promise.all([
        newsletterService.get(campaign.id),
        newsletterService.recipients(campaign.id, { pageSize: 100 }),
      ]);
      setDetail(full);
      setRecipients(recips.results || []);
      setRecipientBreakdown(recips.breakdown || null);
      detailDisclosure.onOpen();
    } catch {
      toast({ title: t('newsletters.toasts.loadOneFailed'), status: 'error', duration: 3000 });
    }
  };

  const recipientStatusLabels = {
    pending: t('newsletters.recipientStatus.pending'),
    sent: t('newsletters.recipientStatus.sent'),
    failed: t('newsletters.recipientStatus.failed'),
    skipped: t('newsletters.recipientStatus.skipped'),
  };

  return (
    <PageShell
      title={t('newsletters.title')}
      subtitle={t('newsletters.subtitle', { count: total })}
      breadcrumbs={[{ label: t('newsletters.title') }]}
      loading={loading} error={error}
      actions={
        <HStack spacing={2}>
          <Button size="sm" variant="outline" onClick={optOutDisclosure.onOpen}>
            {t('newsletters.buttons.unsubscribes')}
          </Button>
          <Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={openCreate}>
            {t('newsletters.buttons.newCampaign')}
          </Button>
        </HStack>
      }
    >
      {/* Filters */}
      <HStack mb={4} spacing={3}>
        <Input size="sm" maxW="320px" placeholder={t('newsletters.filters.searchPlaceholder')}
          value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        <Select size="sm" maxW="200px" value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">{t('newsletters.filters.allStatuses')}</option>
          <option value="draft">{t('newsletters.status.draft')}</option>
          <option value="scheduled">{t('newsletters.status.scheduled')}</option>
          <option value="sending">{t('newsletters.status.sending')}</option>
          <option value="sent">{t('newsletters.status.sent')}</option>
          <option value="failed">{t('newsletters.status.failed')}</option>
          <option value="cancelled">{t('newsletters.status.cancelled')}</option>
        </Select>
      </HStack>

      <Card shadow="sm" borderWidth="1px">
        <CardBody p={0}>
          <TableContainer>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>{t('newsletters.columns.campaign')}</Th><Th>{t('newsletters.columns.status')}</Th>
                  <Th>{t('newsletters.columns.recipients')}</Th>
                  <Th>{t('newsletters.columns.sent')}</Th><Th>{t('newsletters.columns.failed')}</Th>
                  <Th>{t('newsletters.columns.created')}</Th><Th>{t('newsletters.columns.actions')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {campaigns.map((c) => (
                  <Tr key={c.id} _hover={{ bg: 'gray.50' }} cursor="pointer"
                    onClick={() => openDetail(c)}>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" fontWeight="500">{c.name}</Text>
                        <Text fontSize="2xs" color="gray.400" noOfLines={1}>{c.subject}</Text>
                      </VStack>
                    </Td>
                    <Td>
                      <Badge colorScheme={STATUS_COLORS[c.status] || 'gray'} fontSize="xs">
                        {c.statusLabel}
                      </Badge>
                      {c.status === 'scheduled' && c.scheduledAt && (
                        <Text fontSize="2xs" color="gray.400" mt={1}>
                          {new Date(c.scheduledAt).toLocaleString()}
                        </Text>
                      )}
                    </Td>
                    <Td fontSize="sm">{c.totalRecipients}</Td>
                    <Td fontSize="sm" color="green.600">{c.sentCount}</Td>
                    <Td fontSize="sm" color={c.failedCount ? 'red.500' : 'gray.400'}>
                      {c.failedCount}
                    </Td>
                    <Td fontSize="sm">{new Date(c.createdAt).toLocaleDateString()}</Td>
                    <Td onClick={(e) => e.stopPropagation()}>
                      <HStack spacing={1}>
                        <Tooltip label={t('newsletters.actions.view')}>
                          <IconButton size="xs" icon={<ViewIcon />} variant="ghost"
                            aria-label={t('newsletters.actions.view')} onClick={() => openDetail(c)} />
                        </Tooltip>
                        {c.isEditable && (
                          <Tooltip label={t('newsletters.actions.edit')}>
                            <IconButton size="xs" icon={<EditIcon />} variant="ghost"
                              aria-label={t('newsletters.actions.edit')} onClick={() => openEdit(c)} />
                          </Tooltip>
                        )}
                        {c.isEditable && (
                          <Tooltip label={t('newsletters.actions.sendNow')}>
                            <IconButton size="xs" icon={<EmailIcon />} variant="ghost"
                              colorScheme="blue" aria-label={t('newsletters.actions.send')}
                              onClick={() => openSendConfirm(c)} />
                          </Tooltip>
                        )}
                        <Tooltip label={t('newsletters.actions.duplicate')}>
                          <IconButton size="xs" icon={<RepeatIcon />} variant="ghost"
                            aria-label={t('newsletters.actions.duplicate')} onClick={() => handleDuplicate(c)} />
                        </Tooltip>
                        {c.status !== 'sending' && (
                          <Tooltip label={t('newsletters.actions.delete')}>
                            <IconButton size="xs" icon={<DeleteIcon />} variant="ghost"
                              colorScheme="red" aria-label={t('newsletters.actions.delete')}
                              onClick={() => { setDeleteTarget(c); deleteDisclosure.onOpen(); }} />
                          </Tooltip>
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                ))}
                {campaigns.length === 0 && (
                  <Tr>
                    <Td colSpan={7} textAlign="center" py={10} color="gray.400">
                      {t('newsletters.emptyState.noCampaigns')}
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>

      {totalPages > 1 && (
        <HStack justify="space-between" mt={4}>
          <Text fontSize="sm" color="gray.500">
            {t('newsletters.pagination.showing', {
              from: (page - 1) * PAGE_SIZE + 1, to: Math.min(page * PAGE_SIZE, total), total,
            })}
          </Text>
          <HStack spacing={2}>
            <IconButton size="sm" icon={<ChevronLeftIcon />} variant="outline"
              isDisabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label={t('newsletters.pagination.previous')} />
            <Text fontSize="sm" fontWeight="500">
              {t('newsletters.pagination.pageOf', { page, totalPages })}
            </Text>
            <IconButton size="sm" icon={<ChevronRightIcon />} variant="outline"
              isDisabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} aria-label={t('newsletters.pagination.next')} />
          </HStack>
        </HStack>
      )}

      {/* ===== Campaign editor ===== */}
      <Drawer isOpen={editorDisclosure.isOpen} onClose={editorDisclosure.onClose} size="xl">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>{editingId ? t('newsletters.editor.editTitle') : t('newsletters.buttons.newCampaign')}</DrawerHeader>
          <DrawerBody>
            <Tabs size="sm" variant="enclosed">
              <TabList>
                <Tab>{t('newsletters.editor.tabs.content')}</Tab>
                <Tab>{t('newsletters.editor.tabs.audience')}</Tab>
                <Tab>{t('newsletters.editor.tabs.previewTest')}</Tab>
              </TabList>
              <TabPanels>
                {/* Content */}
                <TabPanel px={0}>
                  <VStack spacing={4} align="stretch">
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <FormControl isRequired>
                        <FormLabel fontSize="sm">{t('newsletters.editor.nameLabel')}</FormLabel>
                        <Input size="sm" value={form.name} onChange={setF('name')}
                          placeholder={t('newsletters.editor.namePlaceholder')} />
                        <FormHelperText fontSize="xs">{t('newsletters.editor.nameHelp')}</FormHelperText>
                      </FormControl>
                      <FormControl isRequired>
                        <FormLabel fontSize="sm">{t('newsletters.editor.subjectLabel')}</FormLabel>
                        <Input size="sm" value={form.subject} onChange={setF('subject')}
                          placeholder={t('newsletters.editor.subjectPlaceholder', { firstNameTag: '{{guest_first_name}}' })} />
                      </FormControl>
                    </SimpleGrid>

                    <FormControl>
                      <FormLabel fontSize="sm">{t('newsletters.editor.preheaderLabel')}</FormLabel>
                      <Input size="sm" value={form.preheader} onChange={setF('preheader')}
                        placeholder={t('newsletters.editor.preheaderPlaceholder')} />
                    </FormControl>

                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                      <FormControl>
                        <FormLabel fontSize="sm">{t('newsletters.editor.fromNameLabel')}</FormLabel>
                        <Input size="sm" value={form.fromName} onChange={setF('fromName')}
                          placeholder={t('newsletters.editor.defaultsToHotel')} />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">{t('newsletters.editor.fromEmailLabel')}</FormLabel>
                        <Input size="sm" type="email" value={form.fromEmail}
                          onChange={setF('fromEmail')} placeholder={t('newsletters.editor.defaultsToHotel')} />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">{t('newsletters.editor.replyToLabel')}</FormLabel>
                        <Input size="sm" type="email" value={form.replyTo}
                          onChange={setF('replyTo')} placeholder={t('newsletters.editor.optional')} />
                      </FormControl>
                    </SimpleGrid>

                    <EmailBlockEditor
                      value={form}
                      onChange={(next) => setForm((prev) => ({ ...prev, ...next }))}
                    />

                    <Box>
                      <Text fontSize="xs" color="gray.500" mb={1}>
                        {t('newsletters.editor.personalisationIntro')}
                      </Text>
                      <Wrap spacing={1}>
                        {placeholders.map((p) => (
                          <WrapItem key={p.code}>
                            <Tooltip label={t('newsletters.editor.placeholderTooltip', { label: p.label, sample: p.sample })}>
                              <Tag size="sm" colorScheme="blue">{`{{${p.code}}}`}</Tag>
                            </Tooltip>
                          </WrapItem>
                        ))}
                      </Wrap>
                      <Text fontSize="xs" color="gray.400" mt={1}>
                        {t('newsletters.editor.personalisationHelp')}
                      </Text>
                    </Box>
                  </VStack>
                </TabPanel>

                {/* Audience */}
                <TabPanel px={0}>
                  <AudienceFilterBuilder
                    filters={form.audienceFilters}
                    onChange={(next) => setForm((prev) => ({ ...prev, audienceFilters: next }))}
                  />
                </TabPanel>

                {/* Preview & test */}
                <TabPanel px={0}>
                  <VStack spacing={4} align="stretch">
                    <HStack>
                      <Button size="sm" onClick={refreshPreview} isLoading={previewing}>
                        {t('newsletters.preview.render')}
                      </Button>
                      {preview && (
                        <Text fontSize="xs" color="gray.500">
                          {t('newsletters.preview.renderedFor', {
                            name: preview.sampleGuest?.name || t('newsletters.preview.sampleGuest'),
                            fromName: preview.fromName,
                            fromEmail: preview.fromEmail,
                          })}
                        </Text>
                      )}
                    </HStack>

                    {preview && (
                      <Box borderWidth="1px" borderRadius="md" overflow="hidden">
                        <Box p={3} bg="gray.50" borderBottomWidth="1px">
                          <Text fontSize="sm" fontWeight="600">{preview.subject}</Text>
                          {preview.preheader && (
                            <Text fontSize="xs" color="gray.500">{preview.preheader}</Text>
                          )}
                        </Box>
                        <Box
                          as="iframe"
                          title={t('newsletters.preview.iframeTitle')}
                          srcDoc={preview.html}
                          width="100%"
                          height="420px"
                          border="0"
                          // Sandboxed with no allow-scripts: the body is
                          // operator-authored HTML and must not run in the
                          // admin panel's origin.
                          sandbox=""
                        />
                      </Box>
                    )}

                    <Divider />

                    <FormControl>
                      <FormLabel fontSize="sm">{t('newsletters.preview.testLabel')}</FormLabel>
                      <HStack>
                        <Input size="sm" type="email" maxW="320px" value={testEmail}
                          onChange={(e) => setTestEmail(e.target.value)}
                          placeholder={t('newsletters.preview.testPlaceholder')} />
                        <Button size="sm" onClick={handleTestSend} isLoading={saving}>
                          {t('newsletters.preview.sendTest')}
                        </Button>
                      </HStack>
                      <FormHelperText fontSize="xs">
                        {t('newsletters.preview.testHelp')}
                      </FormHelperText>
                    </FormControl>
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </DrawerBody>
          <DrawerFooter borderTopWidth="1px">
            <HStack w="100%" justify="space-between">
              <Button size="sm" variant="ghost" onClick={editorDisclosure.onClose}>
                {t('newsletters.buttons.close')}
              </Button>
              <HStack>
                <Button size="sm" onClick={handleSaveAndClose} isLoading={saving}>
                  {t('newsletters.buttons.saveDraft')}
                </Button>
                <Button size="sm" colorScheme="blue" isLoading={saving}
                  onClick={async () => {
                    const saved = await handleSave();
                    if (saved) openSendConfirm(saved);
                  }}>
                  {t('newsletters.buttons.saveAndSend')}
                </Button>
              </HStack>
            </HStack>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* ===== Campaign detail ===== */}
      <Drawer isOpen={detailDisclosure.isOpen} onClose={detailDisclosure.onClose} size="lg">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>{detail?.name}</DrawerHeader>
          <DrawerBody>
            {detail && (
              <VStack align="stretch" spacing={4}>
                <HStack>
                  <Badge colorScheme={STATUS_COLORS[detail.status]}>{detail.statusLabel}</Badge>
                  {detail.completedAt && (
                    <Text fontSize="xs" color="gray.500">
                      {t('newsletters.detail.finishedAt', { date: new Date(detail.completedAt).toLocaleString() })}
                    </Text>
                  )}
                </HStack>

                {detail.lastError && (
                  <Alert status="error" borderRadius="md" fontSize="sm">
                    <AlertIcon />{detail.lastError}
                  </Alert>
                )}

                <SimpleGrid columns={4} spacing={3}>
                  <Stat><StatLabel fontSize="xs">{t('newsletters.columns.recipients')}</StatLabel>
                    <StatNumber fontSize="lg">{detail.totalRecipients}</StatNumber></Stat>
                  <Stat><StatLabel fontSize="xs">{t('newsletters.columns.sent')}</StatLabel>
                    <StatNumber fontSize="lg" color="green.600">{detail.sentCount}</StatNumber></Stat>
                  <Stat><StatLabel fontSize="xs">{t('newsletters.columns.failed')}</StatLabel>
                    <StatNumber fontSize="lg" color={detail.failedCount ? 'red.500' : 'gray.400'}>
                      {detail.failedCount}</StatNumber></Stat>
                  <Stat><StatLabel fontSize="xs">{t('newsletters.detail.skipped')}</StatLabel>
                    <StatNumber fontSize="lg" color="gray.500">{detail.skippedCount}</StatNumber></Stat>
                </SimpleGrid>

                {detail.status === 'sending' && detail.totalRecipients > 0 && (
                  <Box>
                    <Progress size="sm" borderRadius="md" hasStripe isAnimated
                      value={((detail.sentCount + detail.failedCount) / detail.totalRecipients) * 100} />
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {t('newsletters.detail.processed', {
                        processed: detail.sentCount + detail.failedCount, total: detail.totalRecipients,
                      })}
                    </Text>
                  </Box>
                )}

                <Divider />

                <Box>
                  <Text fontSize="xs" color="gray.400">{t('newsletters.detail.subject')}</Text>
                  <Text fontSize="sm">{detail.subject}</Text>
                </Box>

                <Box>
                  <Text fontSize="xs" color="gray.400" mb={1}>{t('newsletters.detail.audienceFilters')}</Text>
                  {Object.keys(detail.audienceFilters || {}).length === 0 ? (
                    <Text fontSize="sm">{t('newsletters.detail.everyone')}</Text>
                  ) : (
                    <Wrap spacing={1}>
                      {Object.entries(detail.audienceFilters).map(([k, v]) => (
                        <WrapItem key={k}>
                          <Tag size="sm">{k}: {Array.isArray(v) ? v.join(', ') : String(v)}</Tag>
                        </WrapItem>
                      ))}
                    </Wrap>
                  )}
                </Box>

                <Divider />

                <Box>
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" fontWeight="600">{t('newsletters.detail.deliveryLog')}</Text>
                    {recipientBreakdown && (
                      <HStack fontSize="xs" color="gray.500" spacing={3}>
                        <Text>{t('newsletters.detail.sentCount', { count: recipientBreakdown.sent })}</Text>
                        <Text>{t('newsletters.detail.failedCount', { count: recipientBreakdown.failed })}</Text>
                        <Text>{t('newsletters.detail.pendingCount', { count: recipientBreakdown.pending })}</Text>
                      </HStack>
                    )}
                  </HStack>
                  <TableContainer maxH="320px" overflowY="auto">
                    <Table size="sm">
                      <Thead position="sticky" top={0} bg="white" zIndex={1}>
                        <Tr>
                          <Th>{t('newsletters.columns.email')}</Th><Th>{t('newsletters.columns.status')}</Th>
                          <Th>{t('newsletters.columns.detail')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {recipients.map((r) => (
                          <Tr key={r.id}>
                            <Td fontSize="xs">{r.email}</Td>
                            <Td>
                              <Badge fontSize="2xs"
                                colorScheme={RECIPIENT_STATUS_COLORS[r.status] || 'gray'}>
                                {recipientStatusLabels[r.status] || r.status}
                              </Badge>
                            </Td>
                            <Td fontSize="2xs" color="gray.500" maxW="220px" isTruncated>
                              {r.errorMessage || r.skipReason ||
                                (r.sentAt ? new Date(r.sentAt).toLocaleString() : '')}
                            </Td>
                          </Tr>
                        ))}
                        {recipients.length === 0 && (
                          <Tr><Td colSpan={3} textAlign="center" py={6} color="gray.400">
                            {t('newsletters.emptyState.nothingSent')}
                          </Td></Tr>
                        )}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              </VStack>
            )}
          </DrawerBody>
          <DrawerFooter borderTopWidth="1px">
            {detail && (
              <HStack w="100%" spacing={2}>
                {detail.isEditable && (
                  <Button size="sm" variant="outline"
                    onClick={() => { detailDisclosure.onClose(); openEdit(detail); }}>
                    {t('newsletters.actions.edit')}
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => handleDuplicate(detail)}>
                  {t('newsletters.actions.duplicate')}
                </Button>
                {['scheduled', 'sending'].includes(detail.status) && (
                  <Button size="sm" colorScheme="orange" variant="outline"
                    onClick={() => handleCancel(detail)}>
                    {t('newsletters.buttons.cancelSend')}
                  </Button>
                )}
              </HStack>
            )}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* ===== Send confirmation ===== */}
      <Modal isOpen={sendDisclosure.isOpen} onClose={sendDisclosure.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('newsletters.sendConfirm.title')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Text fontSize="sm">
                <Trans
                  i18nKey="newsletters.sendConfirm.body"
                  values={{ name: sendTarget?.name }}
                  components={{ b: <strong /> }}
                />
              </Text>
              <Alert status="info" borderRadius="md" fontSize="sm">
                <AlertIcon />
                {t('newsletters.sendConfirm.optOutNote')}
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={sendDisclosure.onClose}>{t('newsletters.buttons.cancel')}</Button>
            <Button colorScheme="blue" onClick={handleSend} isLoading={saving}>
              {t('newsletters.actions.sendNow')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmModal
        isOpen={deleteDisclosure.isOpen}
        onClose={deleteDisclosure.onClose}
        onConfirm={handleDelete}
        title={t('newsletters.deleteConfirm.title')}
        message={t('newsletters.deleteConfirm.message', { name: deleteTarget?.name })}
        confirmLabel={t('newsletters.actions.delete')}
      />

      <OptOutDrawer isOpen={optOutDisclosure.isOpen} onClose={optOutDisclosure.onClose} />
    </PageShell>
  );
};

/** Suppression list — who has unsubscribed, and manual add/remove. */
const OptOutDrawer = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      const data = await newsletterService.optOuts({ pageSize: 100 });
      setRows(data.results || []);
      setTotal(data.total || 0);
    } catch {
      setRows([]);
    }
  }, []);

  useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

  const add = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      const res = await newsletterService.addOptOut({ email: email.trim() });
      toast({ title: res.message, status: 'success', duration: 3000 });
      setEmail('');
      load();
    } catch (err) {
      toast({ title: err.response?.data?.detail || t('newsletters.optOuts.addFailed'),
              status: 'error', duration: 3000 });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    try {
      await newsletterService.removeOptOut(row.id);
      toast({ title: t('newsletters.optOuts.resubscribed', { email: row.email }), status: 'info', duration: 2000 });
      load();
    } catch {
      toast({ title: t('newsletters.optOuts.removeFailed'), status: 'error', duration: 3000 });
    }
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>{t('newsletters.optOuts.title', { total })}</DrawerHeader>
        <DrawerBody>
          <VStack align="stretch" spacing={4}>
            <Alert status="info" borderRadius="md" fontSize="sm">
              <AlertIcon />
              {t('newsletters.optOuts.info')}
            </Alert>

            <FormControl>
              <FormLabel fontSize="sm">{t('newsletters.optOuts.addLabel')}</FormLabel>
              <HStack>
                <Input size="sm" type="email" value={email} placeholder={t('newsletters.optOuts.addPlaceholder')}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && add()} />
                <Button size="sm" onClick={add} isLoading={busy}>{t('newsletters.optOuts.add')}</Button>
              </HStack>
            </FormControl>

            <TableContainer>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>{t('newsletters.columns.email')}</Th><Th>{t('newsletters.optOuts.source')}</Th>
                    <Th>{t('newsletters.optOuts.when')}</Th><Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {rows.map((r) => (
                    <Tr key={r.id}>
                      <Td fontSize="xs">
                        {r.email}
                        {r.guestName && (
                          <Text fontSize="2xs" color="gray.400">{r.guestName}</Text>
                        )}
                      </Td>
                      <Td fontSize="2xs">{r.sourceLabel}</Td>
                      <Td fontSize="2xs">{new Date(r.createdAt).toLocaleDateString()}</Td>
                      <Td>
                        <Button size="xs" variant="ghost" onClick={() => remove(r)}>
                          {t('newsletters.optOuts.resubscribe')}
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                  {rows.length === 0 && (
                    <Tr><Td colSpan={4} textAlign="center" py={6} color="gray.400">
                      {t('newsletters.optOuts.empty')}
                    </Td></Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default Newsletters;
